import { useContext } from "react";
import FormContext from "../context/FormContext";
import useAxiosPrivate from "./useAxiosPrivate";
import { getErrorToast } from "../toast";

const useAPI = () => {
  const axiosPrivate = useAxiosPrivate();
  const { setIsLoading } = useContext(FormContext);

  /**
   * Makes an API request using the provided URL, method, data, and configuration.
   * Utilizes axiosPrivate to send the request and handles loading state.
   *
   * @param {string} url - The URL to send the request to.
   * @param {string} [method="GET"] - The HTTP method to use for the request.
   * @param {Object|null} [data=null] - The data to be sent in the request body.
   * @param {Object} [config={}] - Additional Axios configuration options.
   *
   * @returns {Promise<Object|boolean>} - The response data if successful, or false if an error occurs.
   *
   * @throws Will throw an error if the response status is not within the 2xx range.
   */

  const makeAPIRequest = async (
    url,
    method = "GET",
    data = null,
    config = {},
    loader_state = true
  ) => {
    setIsLoading(loader_state);
    try {
      const response = await axiosPrivate({
        url,
        method,
        data,
        ...config, // Allow additional Axios configurations like headers or params
      });

      setIsLoading(false);

      // Handle status checks (common in REST APIs)
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      } else {
        getErrorToast(response.data?.message || "Something went wrong!");
        setIsLoading(false);
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("API Error:", error);
      getErrorToast(error.response?.data?.message || "Something went wrong!");
      return false;
    }
  };

  return {
    getAPI: (url, config, loader_state) =>
      makeAPIRequest(url, "GET", null, config, loader_state),
    postAPI: (url, data, config, loader_state) =>
      makeAPIRequest(url, "POST", data, config, loader_state),
    putAPI: (url, data, config, loader_state) =>
      makeAPIRequest(url, "PUT", data, config, loader_state),
    deleteAPI: (url, config, loader_state) =>
      makeAPIRequest(url, "DELETE", null, config, loader_state),
    patchAPI: (url, data, config, loader_state) =>
      makeAPIRequest(url, "PATCH", data, config, loader_state), // Added PATCH method
  };
};

export default useAPI;
